/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/AnalysisSection.js
 *
 * RECIP 1.0 F-1 — PSMA+ volume mode toggle for plots.
 *
 * F-1 changes from E-4:
 *
 *   1. volumeMode prop added. Same dropdown as FindingSection (shared state
 *      from LongitudinalStudy — user's selection carries across tabs).
 *
 *   2. useEffect for Y-axis keys extended: when isPsmaMode (RECIP +
 *      psma-positive) and VOLUMETRIC, Y-axis keys swap to the PSMA+ fields.
 *      BIDIMENSIONAL always uses diameter keys regardless of volumeMode.
 *
 *   3. Plot data null safety: LinePlot and WaterfallPlot receive
 *      `undefined` instead of `null` for missing data points so charting
 *      libraries gracefully skip them rather than crash. Null values from
 *      sumPsmaPosVolume (NE tps) are replaced with undefined before passing.
 *
 *   4. Grid sizing: 5 cols × xs={2} for RECIP, 4 × xs={3} for non-RECIP.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Body, Grid, Select } from '@platform/ui';
import { LinePlot } from '../plots/LinePlot';
import { WaterfallPlot } from '../plots/WaterfallPlot';
import module from '../../../modules/dicom-measurement/src';
import getRecipTimepointStats from '../../../modules/dicom-measurement/src/assessments/getRecipTimepointStats';

const {
  guideline,
  getTimepoints,
  getTimepointStats,
  getAIAssessments,
} = module;
const { getMeasurementTechniques, getBodyParts, getTrackingTypes } = guideline;

/** Replace null with undefined so chart libraries skip the data point. */
function nullToUndefined(v) {
  return v === null ? undefined : v;
}

export const AnalysisSection = ({
  imageSets,
  ROIContours,
  criteria,
  assessments,
  plotType,
  getFilteredROIContours,
  getRoiUnionExcludingPsma, // accepted, unused in E-state
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  filteredPsmaStatus,
  setFilteredPsmaStatus, // accepted, unused
  selectedLesionNumber,
  setSelectedLesionNumber,
  measurementTechnique,
  setMeasurementTechnique,
  volumeMode = 'all',
  setVolumeMode,
}) => {
  const isRecip = criteria?.guidelineId === 'RECIP10';
  const isPsmaMode = isRecip && volumeMode === 'psma-positive';

  const [xAxisKey] = useState('name');
  const [yAxisKey, setYAxisKey] = useState('comparedSumVolume');
  const [ySumAxisKey, setYSumAxisKey] = useState('sumVolume');

  const timepoints = getTimepoints(imageSets, ROIContours);
  const ai_assessments = getAIAssessments(
    timepoints,
    ROIContours,
    criteria,
    assessments
  );

  const filteredROIContours = getFilteredROIContours(ROIContours);
  const selectedROIContour = ROIContours.find(
    r => r.ROINumber === selectedLesionNumber
  );

  let selectedStats;
  if (isRecip) {
    selectedStats = getRecipTimepointStats(
      timepoints,
      filteredROIContours,
      selectedROIContour ? [selectedROIContour] : [],
      criteria
    );
  } else {
    selectedStats = getTimepointStats(
      timepoints,
      filteredROIContours,
      selectedROIContour ? [selectedROIContour] : [],
      criteria,
      assessments
    );
  }

  // Build plot data — replace null with undefined for null-safe charting
  const measurements = selectedStats.map(series => {
    const summation = { ...series.summation, date: series.date };
    if (isPsmaMode) {
      // Replace null PSMA+ values with undefined so chart libraries skip the point
      if (summation.sumPsmaPosVolume === null)
        summation.sumPsmaPosVolume = undefined;
      if (summation.comparedSumPsmaPosVolume === null)
        summation.comparedSumPsmaPosVolume = undefined;
    }
    return summation;
  });

  const changesFromBaseline = selectedStats.map((series, index) => {
    const change = {
      ...series.fromBaseline,
      date: series.date,
      result: ai_assessments[index]?.overallResponse?.result,
    };
    if (isPsmaMode) {
      if (change.sumPsmaPosVolume === null) change.sumPsmaPosVolume = undefined;
      if (change.comparedSumPsmaPosVolume === null)
        change.comparedSumPsmaPosVolume = undefined;
    }
    return change;
  });

  const onBodyPartChange = evt => setFilteredBodyPart(evt.target.value);
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') setSelectedLesionNumber(-1);
  };
  const onLesionNumberChange = evt =>
    setSelectedLesionNumber(Number(evt.target.value));
  const onTechniqueChange = evt => setMeasurementTechnique(evt.target.value);
  const onVolumeModeChange = evt => setVolumeMode?.(evt.target.value);

  // Update Y-axis keys when measurementTechnique or volumeMode changes
  useEffect(() => {
    switch (measurementTechnique) {
      case 'BIDIMENSIONAL':
        setYAxisKey('comparedSumDiameter');
        setYSumAxisKey('sumDiameter');
        break;
      case 'VOLUMETRIC':
        if (isPsmaMode) {
          setYAxisKey('comparedSumPsmaPosVolume');
          setYSumAxisKey('sumPsmaPosVolume');
        } else {
          setYAxisKey('comparedSumVolume');
          setYSumAxisKey('sumVolume');
        }
        break;
    }
  }, [measurementTechnique, volumeMode, isRecip, isPsmaMode]);

  return (
    <Body>
      <TabPanel value={plotType} index={'measurement'}>
        <LinePlot
          data={measurements}
          xAxisKey={xAxisKey}
          yAxisKey={yAxisKey}
          ySumAxisKey={ySumAxisKey}
        />
      </TabPanel>
      <TabPanel value={plotType} index={'changeFromBaseline'}>
        <WaterfallPlot
          data={changesFromBaseline}
          xAxisKey={xAxisKey}
          yAxisKey={yAxisKey}
          ySumAxisKey={ySumAxisKey}
        />
      </TabPanel>

      {/* Filter row: 5 cols [3,3,2,2,2]=12 for RECIP, 4 cols xs={3} for non-RECIP */}
      <Grid container spacing={2}>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 2 : 3}>
          <Select
            value={selectedLesionNumber}
            onChange={onLesionNumberChange}
            size="small"
            variant="outlined"
            fullWidth
          >
            <option key={-1} value={-1}>
              ------
            </option>
            {(filteredTrackingType === 'selected'
              ? ROIContours
              : filteredROIContours
            ).map(roi => (
              <option key={roi.ROINumber} value={roi.ROINumber}>
                {roi.ROIName}
              </option>
            ))}
          </Select>
        </Grid>
        {/* Volume mode selector — RECIP only */}
        {isRecip && (
          <Grid item xs={2}>
            <Select value={volumeMode} onChange={onVolumeModeChange}>
              <option value="all">All Volume</option>
              <option value="psma-positive">PSMA+ Volume</option>
            </Select>
          </Grid>
        )}
        <Grid item xs={isRecip ? 2 : 3}>
          <Select value={measurementTechnique} onChange={onTechniqueChange}>
            {getMeasurementTechniques().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
AnalysisSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.any,
  assessments: PropTypes.array,
  plotType: PropTypes.string,
  getFilteredROIContours: PropTypes.func,
  getRoiUnionExcludingPsma: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  filteredPsmaStatus: PropTypes.string,
  setFilteredPsmaStatus: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  measurementTechnique: PropTypes.string,
  setMeasurementTechnique: PropTypes.func,
  volumeMode: PropTypes.string,
  setVolumeMode: PropTypes.func,
};

const TabPanel = ({ children, value, index }) => value === index && children;
